import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { fetchBlogIndex } from "@/lib/blogArticles";
import { ARTICLE_CATEGORIES } from "@/lib/articles";
import { motion } from "framer-motion";
import { Pill, Apple, Dumbbell, HeartPulse, FlaskConical, ArrowUpRight, Clock4 } from "lucide-react";

const ICONS = {
  pill: Pill,
  apple: Apple,
  dumbbell: Dumbbell,
  heart: HeartPulse,
  flask: FlaskConical,
};

function ArticleCard({ article, idx }) {
  const { t } = useLang();
  const IconComp = ICONS[article.icon] || Pill;
  const categoryLabel = t.knowledge.categories[article.category] || article.category;

  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.45, delay: Math.min(idx * 0.04, 0.3) }}
      className="group flex flex-col overflow-hidden rounded-3xl border border-zinc-200 bg-white transition hover:border-[color:var(--sentinel-accent)] hover:shadow-[0_24px_60px_-30px_rgba(81,75,186,0.4)]"
      data-testid={`article-card-${article.slug}`}
    >
      <Link to={`/article/${article.slug}`} className="block">
        <div className="relative aspect-[16/10] w-full overflow-hidden bg-zinc-100">
          {article.image ? (
            <img
              src={article.image}
              alt=""
              className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02]"
              loading="lazy"
            />
          ) : (
            <div
              className="absolute inset-0"
              style={{
                background: `linear-gradient(135deg, hsl(${article.hue} 70% 55% / 0.85) 0%, hsl(${article.hue} 80% 75% / 0.6) 100%)`,
              }}
            />
          )}
          <div className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[color:var(--sentinel)] shadow-sm">
            <IconComp className="h-5 w-5" strokeWidth={1.5} />
          </div>
        </div>
        <div className="flex flex-1 flex-col p-5">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-400">
            <span>{categoryLabel}</span>
            <span className="h-0.5 w-3 bg-zinc-300" />
            <span className="inline-flex items-center gap-1">
              <Clock4 className="h-3 w-3" />
              {article.readMins} {t.knowledge.readMins}
            </span>
          </div>
          <h3 className="mt-3 text-lg font-semibold leading-snug text-zinc-900">{article.title}</h3>
          <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-zinc-500">{article.excerpt}</p>
          <span className="mt-4 inline-flex w-fit items-center gap-1 text-xs font-semibold text-[color:var(--sentinel)] transition group-hover:gap-2">
            {t.knowledge.readMore}
            <ArrowUpRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </div>
      </Link>
    </motion.article>
  );
}

export default function KnowledgeCenter() {
  const { t, lang } = useLang();
  const [activeCat, setActiveCat] = useState("all");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchBlogIndex(lang)
      .then((data) => {
        if (!cancelled) {
          setArticles(data.articles || []);
        }
      })
      .catch(() => {
        if (!cancelled) setArticles([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const filtered = useMemo(
    () => (activeCat === "all" ? articles : articles.filter((a) => a.category === activeCat)),
    [articles, activeCat]
  );

  return (
    <section
      id="knowledge"
      data-testid="knowledge-center"
      className="container-x scroll-mt-24 py-24 md:py-32"
    >
      <div className="flex flex-col items-start gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <span className="overline" data-testid="knowledge-overline">
            {t.knowledge.overline}
          </span>
          <h2 className="mt-3 text-4xl text-zinc-900 md:text-[48px] md:leading-[1.04]">
            {t.knowledge.title}
          </h2>
          <p className="mt-3 max-w-2xl text-base text-zinc-600">{t.knowledge.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <CatChip
            active={activeCat === "all"}
            onClick={() => setActiveCat("all")}
            label={t.knowledge.categories.all}
            testId="article-cat-all"
          />
          {ARTICLE_CATEGORIES.map((c) => (
            <CatChip
              key={c}
              active={activeCat === c}
              onClick={() => setActiveCat(c)}
              label={t.knowledge.categories[c]}
              testId={`article-cat-${c}`}
            />
          ))}
        </div>
      </div>

      {loading ? (
        <p className="mt-10 text-center text-sm text-zinc-500">{t.article.loading}</p>
      ) : (
        <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a, i) => (
            <ArticleCard key={a.slug} article={a} idx={i} />
          ))}
        </div>
      )}
    </section>
  );
}

function CatChip({ active, onClick, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center rounded-full border px-3.5 py-1.5 text-xs font-semibold transition ${
        active
          ? "border-[color:var(--sentinel)] bg-[color:var(--sentinel)] text-white"
          : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300"
      }`}
    >
      {label}
    </button>
  );
}
